import { useEffect, useMemo, useState } from "react";
import { apiGet, apiPost, viewReceipt } from "@/lib/api";
import api from "@/lib/api";
import { stickerPaymentBlockedReason } from "@/lib/stickerEligibility";
import { Check, X, Search, ChevronLeft, ChevronRight, Plus } from "lucide-react";

const MAX_RECEIPT_BYTES = 1 * 1024 * 1024;
const PAGE_SIZE_OPTIONS = [10, 20, 50, 80, 100];
const STATUS_FILTERS = ["All", "Pending", "Approved", "Rejected"];
const CATEGORY_FILTERS = [
  { value: "", label: "All categories" },
  { value: "Maintenance Fee", label: "Maintenance Fee" },
  { value: "Sticker fee - Car", label: "Sticker fee - Car" },
  { value: "Sticker fee - Motorcycle", label: "Sticker fee - Motorcycle" },
];

const MANUAL_CATEGORIES = [
  { value: "Maintenance Fee", amount: 80, recurring: true },
  { value: "Sticker fee - Car", amount: 10, recurring: false },
  { value: "Sticker fee - Motorcycle", amount: 5, recurring: false },
];

function iterMonths(startYm, endYm) {
  if (!startYm || !endYm || startYm > endYm) return [];
  const [sy, sm] = startYm.split("-").map(Number);
  const [ey, em] = endYm.split("-").map(Number);
  const months = [];
  let yr = sy;
  let mo = sm;
  while (yr < ey || (yr === ey && mo <= em)) {
    months.push(`${yr}-${String(mo).padStart(2, "0")}`);
    mo += 1;
    if (mo > 12) {
      mo = 1;
      yr += 1;
    }
  }
  return months;
}

function fmtDate(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  return d.toLocaleString("en-MY", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" });
}

function fmtMonth(m) {
  if (!m) return "—";
  const [yr, mo] = m.split("-");
  const labels = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];
  return `${labels[parseInt(mo,10)-1]} ${yr}`;
}

export default function Payments() {
  const [payments, setPayments] = useState([]);
  const [total, setTotal] = useState(0);
  const [pendingCount, setPendingCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [rejectId, setRejectId] = useState(null);
  const [rejectReason, setRejectReason] = useState("");
  const [statusFilter, setStatusFilter] = useState("All");
  const [categoryFilter, setCategoryFilter] = useState("");
  const [submittedFrom, setSubmittedFrom] = useState("");
  const [submittedTo, setSubmittedTo] = useState("");
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(100);
  const [showManual, setShowManual] = useState(false);

  // Debounce the search box so we don't hit the API on every keystroke.
  useEffect(() => {
    const t = setTimeout(() => {
      setDebouncedSearch(search.trim());
      setPage(1);
    }, 350);
    return () => clearTimeout(t);
  }, [search]);

  // Server-side pagination/filtering/search: only the current page of rows is ever
  // fetched, instead of loading the whole (growing) payments table into the browser.
  useEffect(() => {
    fetchPayments({
      page, pageSize, status: statusFilter, search: debouncedSearch,
      category: categoryFilter, submittedFrom, submittedTo,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, pageSize, statusFilter, debouncedSearch, categoryFilter, submittedFrom, submittedTo]);

  async function fetchPayments({ page, pageSize, status, search, category, submittedFrom, submittedTo }) {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      params.set("page", String(page));
      params.set("pageSize", String(pageSize));
      if (status && status !== "All") params.set("status", status.toLowerCase());
      if (search) params.set("search", search);
      if (category) params.set("category", category);
      if (submittedFrom) params.set("submitted_from", submittedFrom);
      if (submittedTo) params.set("submitted_to", submittedTo);
      const res = await apiGet(`/payments?${params.toString()}`);
      const body = res.data;
      setPayments(Array.isArray(body) ? body : body.data ?? []);
      setTotal(Array.isArray(body) ? body.length : body.total ?? 0);
      setPendingCount(Array.isArray(body) ? 0 : body.pendingCount ?? 0);
    } catch (e) {} finally { setLoading(false); }
  }

  async function approve(id) {
    try {
      await api(`/payments/${id}/approve`, { method: "PUT" });
      // Instant visual feedback, then refetch to keep filtered list/pagination/counts in sync
      // (e.g. an approved row should drop off a "Pending" filtered page).
      setPayments(prev => prev.map(p => p.id === id ? { ...p, status: "approved" } : p));
      setPendingCount(c => Math.max(0, c - 1));
      fetchPayments({
        page, pageSize, status: statusFilter, search: debouncedSearch,
        category: categoryFilter, submittedFrom, submittedTo,
      });
    } catch (e) {}
  }

  async function reject(id) {
    try {
      await api(`/payments/${id}/reject`, { method: "PUT", data: { reason: rejectReason || "Payment rejected" } });
      setPayments(prev => prev.map(p => p.id === id ? { ...p, status: "rejected", reject_reason: rejectReason } : p));
      setPendingCount(c => Math.max(0, c - 1));
      setRejectId(null); setRejectReason("");
      fetchPayments({
        page, pageSize, status: statusFilter, search: debouncedSearch,
        category: categoryFilter, submittedFrom, submittedTo,
      });
    } catch (e) {}
  }

  const totalPages = Math.max(1, Math.ceil(total / pageSize));
  const safePage = Math.min(page, totalPages);
  const pageData = payments;

  function changePageSize(next) {
    setPageSize(next);
    setPage(1);
  }

  function renderActions(p) {
    if (p.status === "pending") {
      return (
        <div style={{ display: "flex", gap: 4, flexWrap: "wrap" }}>
          {p.receipt_id && (
            <button type="button" className="btn" style={{ fontSize: 10 }}
              onClick={() => viewReceipt(p.receipt_id)} data-testid={`view-receipt-${p.id}`}>Receipt</button>
          )}
          <button className="btn btn-approve" onClick={() => approve(p.id)} data-testid={`approve-payment-${p.id}`}><Check size={11} /> Approve</button>
          <button className="btn btn-reject"  onClick={() => setRejectId(p.id)} data-testid={`reject-payment-${p.id}`}><X size={11} /> Reject</button>
        </div>
      );
    }
    if (p.status === "approved") {
      return (
        <div style={{ display: "flex", gap: 4, alignItems: "center", flexWrap: "wrap" }}>
          <span style={{ fontSize: 10, color: "var(--success)" }}>Verified ✓</span>
          {p.receipt_id && (
            <button type="button" className="btn" style={{ fontSize: 10 }}
              onClick={() => viewReceipt(p.receipt_id)}>Receipt</button>
          )}
        </div>
      );
    }
    return <span style={{ fontSize: 10, color: "var(--danger)" }}>{p.reject_reason || "Rejected"}</span>;
  }

  return (
    <div>
      <div className="section-panel" data-testid="payments-panel">
        <div className="section-head">
          <div className="section-title">Payment Verification</div>
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <span style={{ fontSize: 11, color: pendingCount > 0 ? "var(--pending)" : "var(--text2)", fontWeight: 500 }}>
              {pendingCount} pending
            </span>
            <button
              type="button"
              className="btn btn-primary"
              style={{ fontSize: 11, padding: "4px 10px" }}
              onClick={() => setShowManual(true)}
              data-testid="open-manual-payment-btn"
            >
              <Plus size={12} /> Record Manual Payment
            </button>
          </div>
        </div>

        {/* Filters */}
        <div style={{ display: "flex", gap: 8, padding: "10px 18px", borderBottom: "1px solid var(--border)", flexWrap: "wrap", alignItems: "center" }}>
          <div style={{ display: "flex", gap: 4, flexWrap: "wrap" }}>
            {STATUS_FILTERS.map(s => (
              <button key={s}
                className={`btn ${statusFilter === s ? "btn-primary" : ""}`}
                style={{ fontSize: 10, padding: "3px 9px" }}
                onClick={() => { setStatusFilter(s); setPage(1); }}
                data-testid={`filter-${s.toLowerCase()}`}>
                {s}
              </button>
            ))}
          </div>
          <label style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 11, color: "var(--text2)" }}>
            Category
            <select
              className="form-input"
              style={{ fontSize: 11, height: 28, width: "auto", padding: "2px 8px" }}
              value={categoryFilter}
              onChange={e => { setCategoryFilter(e.target.value); setPage(1); }}
              data-testid="payments-category-filter"
            >
              {CATEGORY_FILTERS.map(c => (
                <option key={c.value || "all"} value={c.value}>{c.label}</option>
              ))}
            </select>
          </label>
          <label style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 11, color: "var(--text2)" }}>
            From
            <input
              type="date"
              className="form-input"
              style={{ fontSize: 11, height: 28, width: "auto", padding: "2px 8px" }}
              value={submittedFrom}
              max={submittedTo || undefined}
              onChange={e => { setSubmittedFrom(e.target.value); setPage(1); }}
              data-testid="payments-submitted-from"
            />
          </label>
          <label style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 11, color: "var(--text2)" }}>
            To
            <input
              type="date"
              className="form-input"
              style={{ fontSize: 11, height: 28, width: "auto", padding: "2px 8px" }}
              value={submittedTo}
              min={submittedFrom || undefined}
              onChange={e => { setSubmittedTo(e.target.value); setPage(1); }}
              data-testid="payments-submitted-to"
            />
          </label>
          {(submittedFrom || submittedTo || categoryFilter) && (
            <button
              type="button"
              className="btn"
              style={{ fontSize: 10, padding: "3px 9px" }}
              onClick={() => { setSubmittedFrom(""); setSubmittedTo(""); setCategoryFilter(""); setPage(1); }}
              data-testid="payments-clear-filters"
            >
              Clear dates / category
            </button>
          )}
          <div className="head-search-box" style={{ position: "relative", marginLeft: "auto" }}>
            <Search size={11} style={{ position: "absolute", left: 8, top: "50%", transform: "translateY(-50%)", color: "var(--text2)" }} />
            <input
              className="form-input head-search-input"
              style={{ paddingLeft: 26, fontSize: 11, height: 28, width: 200 }}
              placeholder="Search name / house / month / bill…"
              value={search}
              onChange={e => setSearch(e.target.value)}
              data-testid="payments-search"
            />
          </div>
        </div>

        {loading ? (
          <div style={{ padding: 24, textAlign: "center", color: "var(--text2)", fontSize: 12 }}>Loading payments...</div>
        ) : (
          <>
          {/* Desktop / tablet table */}
          <div className="payments-table-wrap" style={{ overflowX: "auto" }}>
            <table className="percasa-table">
              <thead>
                <tr>
                  <th>Resident</th><th>House Alias</th><th>Category</th>
                  <th>Month</th><th>Amount</th><th>Submitted</th>
                  <th>Remarks</th><th>Bill Code</th><th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {pageData.length === 0 ? (
                  <tr><td colSpan={9} style={{ textAlign: "center", color: "var(--text2)", padding: 24 }}>No payments found</td></tr>
                ) : pageData.map(p => (
                  <tr key={p.id}>
                    <td style={{ fontWeight: 500 }}>{p.resident_name}</td>
                    <td><span className="mono">{p.house_alias || p.unit || "—"}</span></td>
                    <td style={{ fontSize: 11 }}>{p.category}</td>
                    <td><span className="mono">{fmtMonth(p.month)}</span></td>
                    <td><span className="mono" style={{ color: "var(--text)" }}>RM {p.amount?.toFixed(2)}</span></td>
                    <td style={{ fontSize: 10, color: "var(--text2)" }}>{fmtDate(p.submitted_at)}</td>
                    <td style={{ fontSize: 11, whiteSpace: "normal", wordBreak: "break-word", maxWidth: 320 }}>{p.remarks || "—"}</td>
                    <td><span className="mono" style={{ fontSize: 10 }}>{p.billcode || "—"}</span></td>
                    <td>{renderActions(p)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Mobile card list */}
          <div className="payments-card-list" data-testid="admin-payments-card-list">
            {pageData.length === 0 ? (
              <div style={{ padding: 24, textAlign: "center", color: "var(--text2)", fontSize: 12 }}>No payments found</div>
            ) : pageData.map(p => (
              <div key={p.id} className="payment-card">
                <div className="payment-card-top">
                  <span style={{ fontWeight: 600 }}>{p.resident_name}</span>
                  <span className="mono" style={{ fontSize: 10 }}>{p.billcode || "—"}</span>
                </div>
                <div className="payment-card-row">
                  <span>House <span className="mono" style={{ fontWeight: 600 }}>{p.house_alias || p.unit || "—"}</span> · {p.category}</span>
                  <span className="mono" style={{ fontWeight: 600 }}>RM {p.amount?.toFixed(2)}</span>
                </div>
                <div className="payment-card-meta">
                  <span>Month: <span className="mono">{fmtMonth(p.month)}</span></span>
                  <span>Submitted {fmtDate(p.submitted_at)}</span>
                </div>
                {p.remarks && (
                  <div style={{ marginTop: 8, fontSize: 11, color: "var(--text)", whiteSpace: "normal", wordBreak: "break-word" }}>
                    {p.remarks}
                  </div>
                )}
                <div style={{ marginTop: 8 }}>{renderActions(p)}</div>
              </div>
            ))}
          </div>
          </>
        )}

        {/* Pagination */}
        {!loading && total > 0 && (
          <div className="pagination-row" style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12, padding: "10px 18px", borderTop: "1px solid var(--border)", fontSize: 11, flexWrap: "wrap" }}>
            <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
              <span style={{ color: "var(--text2)" }}>
                Showing {(safePage - 1) * pageSize + 1}–{Math.min(safePage * pageSize, total)} of {total}
              </span>
              <label style={{ display: "flex", alignItems: "center", gap: 6, color: "var(--text2)" }}>
                Rows
                <select
                  className="form-input"
                  style={{ fontSize: 11, height: 28, width: "auto", padding: "2px 8px" }}
                  value={pageSize}
                  onChange={e => changePageSize(Number(e.target.value))}
                  data-testid="payments-page-size"
                >
                  {PAGE_SIZE_OPTIONS.map(n => (
                    <option key={n} value={n}>{n}</option>
                  ))}
                </select>
              </label>
            </div>
            {totalPages > 1 && (
              <div style={{ display: "flex", gap: 4, flexWrap: "wrap" }}>
                <button className="btn" onClick={() => setPage(Math.max(1, safePage - 1))} disabled={safePage === 1} data-testid="payments-prev-page">
                  <ChevronLeft size={12} />
                </button>
                {Array.from({ length: totalPages }, (_, i) => i + 1).map(pg => (
                  <button key={pg} className={`btn ${pg === safePage ? "btn-primary" : ""}`} style={{ minWidth: 28 }}
                    onClick={() => setPage(pg)} data-testid={`payments-page-${pg}`}>
                    {pg}
                  </button>
                ))}
                <button className="btn" onClick={() => setPage(Math.min(totalPages, safePage + 1))} disabled={safePage === totalPages} data-testid="payments-next-page">
                  <ChevronRight size={12} />
                </button>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Reject Modal */}
      {rejectId && (
        <div className="modal-overlay">
          <div className="modal-box" data-testid="reject-modal">
            <div className="modal-title">Reject Payment</div>
            <div className="form-group">
              <label className="form-label">Rejection reason</label>
              <textarea className="form-input" rows={3} placeholder="e.g. Wrong receipt, unclear image..." value={rejectReason} onChange={e => setRejectReason(e.target.value)} data-testid="reject-reason-input" />
            </div>
            <div className="modal-actions">
              <button className="btn" onClick={() => { setRejectId(null); setRejectReason(""); }} data-testid="cancel-reject-btn">Cancel</button>
              <button className="btn btn-reject" onClick={() => reject(rejectId)} data-testid="confirm-reject-btn"><X size={11} /> Reject Payment</button>
            </div>
          </div>
        </div>
      )}

      {showManual && (
        <ManualPaymentForm
          onClose={() => setShowManual(false)}
          onRecorded={() => {
            setShowManual(false);
            fetchPayments({
              page, pageSize, status: statusFilter, search: debouncedSearch,
              category: categoryFilter, submittedFrom, submittedTo,
            });
          }}
        />
      )}
    </div>
  );
}

function ManualPaymentForm({ onRecorded, onClose }) {
  const today = new Date().toISOString().slice(0, 10);
  const thisMonth = new Date().toISOString().slice(0, 7);
  const [houseAlias, setHouseAlias] = useState("");
  const [resident, setResident] = useState(null);
  const [lookupError, setLookupError] = useState("");
  const [lookingUp, setLookingUp] = useState(false);
  const [mode, setMode] = useState("single");
  const [month, setMonth] = useState(thisMonth);
  const [startMonth, setStartMonth] = useState(thisMonth);
  const [endMonth, setEndMonth] = useState(thisMonth);
  const [category, setCategory] = useState("Maintenance Fee");
  const [vehiclePlate, setVehiclePlate] = useState("");
  const [submittedAt, setSubmittedAt] = useState(today);
  const [remarks, setRemarks] = useState("");
  const [receiptName, setReceiptName] = useState("");
  const [receiptId, setReceiptId] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  const meta = MANUAL_CATEGORIES.find(c => c.value === category) || MANUAL_CATEGORIES[0];
  const isSticker = category.startsWith("Sticker fee");
  const hasMaintenanceFee = resident?.has_maintenance_fee !== false;
  const stickerBlockedReason = resident
    ? stickerPaymentBlockedReason(category, hasMaintenanceFee)
    : null;
  const selectedMonths = useMemo(
    () => (mode === "multiple" ? iterMonths(startMonth, endMonth) : (month ? [month] : [])),
    [mode, startMonth, endMonth, month]
  );
  const amountPerMonth = meta.amount;
  const totalAmount = amountPerMonth * selectedMonths.length;

  async function searchHouse(e) {
    e?.preventDefault?.();
    const alias = houseAlias.trim();
    setError("");
    setSuccess("");
    if (!alias) {
      setResident(null);
      setLookupError("Enter a house alias to search.");
      return;
    }
    setLookingUp(true);
    setLookupError("");
    try {
      const res = await apiGet(`/residents/lookup?house_alias=${encodeURIComponent(alias)}`);
      setResident(res.data);
    } catch (err) {
      setResident(null);
      setLookupError(err.response?.data?.detail || "House not found.");
    } finally {
      setLookingUp(false);
    }
  }

  function changeCategory(next) {
    const nextMeta = MANUAL_CATEGORIES.find(c => c.value === next) || MANUAL_CATEGORIES[0];
    setCategory(next);
    if (!next.startsWith("Sticker fee")) setVehiclePlate("");
    if (!nextMeta.recurring) setMode("single");
  }

  async function handleReceiptChange(e) {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (file.size > MAX_RECEIPT_BYTES) {
      setError("File too large. Maximum 1MB allowed.");
      return;
    }
    const ext = file.name.split(".").pop()?.toLowerCase() || "";
    if (!["pdf", "jpg", "jpeg", "png"].includes(ext)) {
      setError("Invalid file type. Allowed: PDF, JPG, PNG.");
      return;
    }
    setReceiptName(file.name);
    setReceiptId(null);
    setError("");
    setSuccess("");
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append("file", file);
      const res = await api("/upload/receipt", { method: "POST", data: formData });
      setReceiptId(res.data.file_id);
    } catch (err) {
      const detail = err.response?.data?.detail;
      setError(typeof detail === "string" ? detail : "Failed to upload receipt. Please try again.");
      setReceiptName("");
      setReceiptId(null);
    } finally {
      setUploading(false);
    }
  }

  async function handleSubmit(e) {
    e.preventDefault();
    setError("");
    setSuccess("");
    const alias = houseAlias.trim();
    if (!alias) {
      setError("Enter a house alias and click Search.");
      return;
    }
    if (!resident) {
      setError(lookupError || "Search the house alias first.");
      return;
    }
    if (selectedMonths.length === 0) {
      setError("Select at least one month.");
      return;
    }
    if (selectedMonths.length > 24) {
      setError("Maximum 24 months per submission.");
      return;
    }
    if (!submittedAt) {
      setError("Payment submission date is required.");
      return;
    }
    const plate = vehiclePlate.replace(/[^A-Z0-9]/g, "");
    if (stickerBlockedReason) {
      setError(stickerBlockedReason);
      return;
    }
    if (isSticker && !plate) {
      setError("Vehicle plate number is required for sticker payments.");
      return;
    }

    setSaving(true);
    try {
      const payload = {
        house_alias: resident.house_alias || alias,
        category,
        payment_date: submittedAt,
        remarks: remarks.trim(),
      };
      if (mode === "single") payload.month = month;
      else payload.months = selectedMonths;
      if (isSticker) payload.vehicle_plate_no = plate;
      if (receiptId) payload.receipt_id = receiptId;
      const res = await apiPost("/payments/manual", payload);
      setSuccess(res.data?.message || "Payment recorded.");
      setHouseAlias("");
      setResident(null);
      setVehiclePlate("");
      setRemarks("");
      setReceiptName("");
      setReceiptId(null);
      onRecorded?.();
    } catch (err) {
      const detail = err.response?.data?.detail;
      setError(typeof detail === "string" ? detail : "Failed to record payment.");
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="modal-overlay" onClick={onClose} data-testid="manual-payment-overlay">
      <div
        className="modal-box"
        data-testid="manual-payment-panel"
        onClick={e => e.stopPropagation()}
        style={{ width: 720, maxHeight: "85vh", overflowY: "auto" }}
      >
        <div className="modal-title" style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12 }}>
          Record Manual Payment
          <button type="button" className="btn" onClick={onClose} data-testid="close-manual-payment-btn" aria-label="Close">
            <X size={14} />
          </button>
        </div>
        {error && <div className="auth-error" data-testid="manual-payment-error">{error}</div>}
        {success && <div className="auth-success" data-testid="manual-payment-success">{success}</div>}
        <form onSubmit={handleSubmit}>
          <div className="form-row">
            <div className="form-group">
              <label className="form-label">House Alias *</label>
              <div style={{ display: "flex", gap: 8 }}>
                <input
                  className="form-input"
                  placeholder="e.g. 01-1-1"
                  value={houseAlias}
                  onChange={e => {
                    setHouseAlias(e.target.value);
                    setResident(null);
                    setLookupError("");
                  }}
                  onKeyDown={e => {
                    if (e.key === "Enter") {
                      e.preventDefault();
                      searchHouse();
                    }
                  }}
                  required
                  data-testid="manual-house-alias"
                />
                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={searchHouse}
                  disabled={lookingUp || !houseAlias.trim()}
                  data-testid="manual-house-search-btn"
                  style={{ whiteSpace: "nowrap" }}
                >
                  <Search size={12} /> {lookingUp ? "Searching..." : "Search"}
                </button>
              </div>
              {lookupError && (
                <div style={{ fontSize: 11, color: "var(--danger)", marginTop: 6 }}>{lookupError}</div>
              )}
              {resident && (
                <div
                  className={resident.has_maintenance_fee ? "payment-notice-info" : "payment-notice-warn"}
                  data-testid="manual-maintenance-status"
                  style={{ marginTop: 8, marginBottom: 0 }}
                >
                  {resident.has_maintenance_fee
                    ? "This house has a recorded Maintenance Fee payment. Sticker fees can be recorded."
                    : "This house has no recorded Maintenance Fee payment. Sticker fees cannot be recorded yet."}
                </div>
              )}
            </div>
            <div className="form-group">
              <label className="form-label">Resident Name</label>
              <input className="form-input" value={resident?.name || ""} readOnly style={{ background: "var(--bg)", color: "var(--text2)" }} data-testid="manual-resident-name" />
            </div>
            <div className="form-group">
              <label className="form-label">House No.</label>
              <input className="form-input" value={resident?.unit || ""} readOnly style={{ background: "var(--bg)", color: "var(--text2)" }} data-testid="manual-unit" />
            </div>
            <div className="form-group">
              <label className="form-label">Street</label>
              <input className="form-input" value={resident?.street_number || ""} readOnly style={{ background: "var(--bg)", color: "var(--text2)" }} data-testid="manual-street" />
            </div>
          </div>

          <div style={{ marginBottom: 16 }}>
            <label className="form-label" style={{ marginBottom: 8, display: "block" }}>Submission Type</label>
            <div style={{ display: "flex", gap: 8 }}>
              <button type="button" className={`btn ${mode === "single" ? "btn-primary" : ""}`} onClick={() => setMode("single")} data-testid="manual-mode-single">
                Single Month
              </button>
              <button
                type="button"
                className={`btn ${mode === "multiple" ? "btn-primary" : ""}`}
                onClick={() => setMode("multiple")}
                disabled={!meta.recurring}
                title={!meta.recurring ? "One-time fees use single submission only" : undefined}
                data-testid="manual-mode-multiple"
              >
                Multiple Months
              </button>
            </div>
          </div>

          {mode === "single" ? (
            <div className="form-row">
              <div className="form-group">
                <label className="form-label">Payment Month *</label>
                <input type="month" className="form-input" value={month} onChange={e => setMonth(e.target.value)} required data-testid="manual-month" />
              </div>
              <div className="form-group">
                <label className="form-label">Payment Category *</label>
                <select className="form-input" value={category} onChange={e => changeCategory(e.target.value)} data-testid="manual-category">
                  {MANUAL_CATEGORIES.map(c => (
                    <option key={c.value} value={c.value}>{c.value}</option>
                  ))}
                </select>
              </div>
              <div className="form-group">
                <label className="form-label">Payment Submission Date *</label>
                <input type="date" className="form-input" value={submittedAt} onChange={e => setSubmittedAt(e.target.value)} required data-testid="manual-submitted-at" />
              </div>
            </div>
          ) : (
            <>
              <div className="form-row">
                <div className="form-group">
                  <label className="form-label">From Month *</label>
                  <input type="month" className="form-input" value={startMonth} onChange={e => setStartMonth(e.target.value)} required data-testid="manual-start-month" />
                </div>
                <div className="form-group">
                  <label className="form-label">To Month *</label>
                  <input type="month" className="form-input" value={endMonth} min={startMonth} onChange={e => setEndMonth(e.target.value)} required data-testid="manual-end-month" />
                </div>
                <div className="form-group">
                  <label className="form-label">Payment Category *</label>
                  <select className="form-input" value={category} onChange={e => changeCategory(e.target.value)} data-testid="manual-category-multi">
                    {MANUAL_CATEGORIES.filter(c => c.recurring).map(c => (
                      <option key={c.value} value={c.value}>{c.value}</option>
                    ))}
                  </select>
                </div>
              </div>
              <div className="form-group">
                <label className="form-label">Payment Submission Date *</label>
                <input type="date" className="form-input" value={submittedAt} onChange={e => setSubmittedAt(e.target.value)} required data-testid="manual-submitted-at-multi" />
              </div>
              {selectedMonths.length > 0 && (
                <div className="payment-notice-info" style={{ marginBottom: 16 }} data-testid="manual-months-preview">
                  {selectedMonths.length} payment{selectedMonths.length === 1 ? "" : "s"} will be recorded: {selectedMonths.map(fmtMonth).join(" · ")}
                </div>
              )}
            </>
          )}

          {stickerBlockedReason && (
            <div className="payment-notice-warn" data-testid="manual-sticker-requires-maintenance-notice" style={{ marginBottom: 16 }}>
              {stickerBlockedReason}
            </div>
          )}
          {isSticker && (
            <div className="form-group" data-testid="manual-plate-field">
              <label className="form-label">Vehicle Plate Number *</label>
              <input
                className="form-input"
                placeholder="e.g. WXY1234"
                value={vehiclePlate}
                maxLength={15}
                required
                onChange={e => setVehiclePlate(e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, ""))}
                data-testid="manual-vehicle-plate"
              />
              <div style={{ fontSize: 11, color: "var(--text2)", marginTop: 6 }}>
                Letters and numbers only. Required before recording a sticker payment.
              </div>
            </div>
          )}

          <div className="form-row">
            <div className="form-group">
              <label className="form-label">Amount</label>
              <div className="form-input" style={{ background: "var(--bg)", fontWeight: 600, fontFamily: "var(--mono)", color: "var(--brand-dark)" }} data-testid="manual-amount">
                RM {totalAmount.toFixed(2)}
                {selectedMonths.length > 1 && (
                  <span style={{ fontWeight: 400, color: "var(--text2)", marginLeft: 8, fontFamily: "var(--font)" }}>
                    ({selectedMonths.length} × RM {amountPerMonth.toFixed(2)})
                  </span>
                )}
              </div>
            </div>
          </div>

          <div className="form-group">
            <label className="form-label">
              Receipt
              <span style={{ fontWeight: 400, color: "var(--text2)", marginLeft: 6 }}>(PDF/JPG/PNG, max 1MB)</span>
            </label>
            {mode === "multiple" && (
              <div className="payment-notice-info" data-testid="manual-receipt-notice" style={{ marginBottom: 8 }}>
                One receipt is attached to every month in this submission. Make sure it covers the total amount.
              </div>
            )}
            <div
              style={{
                border: `2px dashed ${receiptId ? "var(--success)" : "var(--border)"}`,
                borderRadius: 8,
                padding: "14px 12px",
                background: receiptId ? "var(--success-light, #f3faf4)" : "var(--bg)",
                cursor: uploading ? "wait" : "pointer",
                textAlign: "center",
              }}
              onClick={() => !uploading && document.getElementById("manual-receipt-upload")?.click()}
              data-testid="manual-receipt-upload-area"
            >
              <input
                id="manual-receipt-upload"
                type="file"
                accept=".pdf,.jpg,.jpeg,.png,application/pdf,image/jpeg,image/png"
                onChange={handleReceiptChange}
                hidden
                data-testid="manual-receipt-file-input"
              />
              {uploading ? (
                <span style={{ fontSize: 12, color: "var(--text2)" }}>Uploading receipt...</span>
              ) : receiptId ? (
                <span style={{ fontSize: 12, color: "var(--success)", fontWeight: 500 }}>{receiptName}</span>
              ) : (
                <span style={{ fontSize: 12, color: "var(--text2)" }}>
                  {receiptName || "Click to upload receipt (optional)"}
                </span>
              )}
            </div>
          </div>

          <div className="form-group">
            <label className="form-label">Remarks (optional)</label>
            <textarea
              className="form-input"
              rows={2}
              placeholder="Any notes for this payment..."
              value={remarks}
              onChange={e => setRemarks(e.target.value)}
              data-testid="manual-remarks"
            />
          </div>

          <div className="modal-actions">
            <button type="button" className="btn" onClick={onClose} data-testid="cancel-manual-payment-btn">Cancel</button>
            <button type="submit" className="btn btn-primary" disabled={saving || lookingUp || uploading || !resident || !!stickerBlockedReason} data-testid="manual-submit-btn">
              {saving ? "Recording..." : "Record Payment"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
